"use client";

import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";
import { format, isAfter, isBefore } from "date-fns";
import { useCalendarDate, useCalendarInput } from "./_hooks/use-calendar-date";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { buttonVariants } from "../../ui/button";
import { cn } from "../../../../../lib/utils";
import { Button } from "../../ui/button";
import SelectDropdownField from "../field/select/select-dropdown-field";
import { Field, FieldContent, FieldTrigger } from "../../ui/field";
import SelectDropdownList from "../field/select/select-dropdown-list";
import ChevronLeft from "../../../../../icons/normal/chevron-left";
import ChevronRight from "../../../../../icons/normal/chevron-right";
import Calendar from "../../../../../icons/normal/calendar";
import { months, years } from "./constant/calendar";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

type LayoutProps = {
  containerClassName?: string;
  textClassName?: string;
  iconClassName?: string;
  iconPosition?: "left" | "right";
  iconSize?: number;
};

type ButtonStyleProps = {
  variant: "transparent" | "filled" | "outline";
  state: "secondary" | "primary" | "cta" | "calendar";
};

type CalendarSelectYearAndMonthSelectProps = CalendarProps & {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  size?: "xsm" | "sm" | "md" | "lg" | "xlg";
  placeholder?: string;
  buttonStyles?: {
    trigger: ButtonStyleProps;
    navigation: ButtonStyleProps;
    action: ButtonStyleProps;
  };
  layout?: LayoutProps;
  minDate?: Date;
  maxDate?: Date;
};

function PopoverCalendar({
  className,
  classNames,
  showOutsideDays = true,
  date,
  onDateChange,
  size = "md",
  placeholder,
  buttonStyles = {
    trigger: { variant: "transparent", state: "secondary" },
    navigation: { variant: "transparent", state: "secondary" },
    action: { variant: "transparent", state: "secondary" },
  },
  layout,
  minDate,
  maxDate,
  ...props
}: CalendarSelectYearAndMonthSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    selectedDate,
    tempSelectedDate,
    currentDate: rawCurrentDate,
    handleYearChange,
    handleMonthChange,
    handleDaySelect,
    handlePrevMonth,
    handleNextMonth,
    handleConfirm,
    handleCancel,
  } = useCalendarDate({
    initialDate: date,
    onDateChange,
  });

  const currentDate =
    rawCurrentDate instanceof Date ? rawCurrentDate : new Date();

  const { inputBuffer } = useCalendarInput({
    selectedDate,
    onDateChange,
    isOpen,
  });

  // Function to determine if a date should be disabled
  const isDateDisabled = (date: Date) => {
    if (minDate && isBefore(date, minDate)) {
      return true;
    }
    if (maxDate && isAfter(date, maxDate)) {
      return true;
    }
    return false;
  };

  // layout의 기본값을 여기서 설정
  const defaultLayout = {
    containerClassName: "flex justify-start",
    textClassName: "",
    iconClassName: "",
    iconPosition: "left",
    iconSize: 24,
    ...layout, // 사용자가 전달한 layout 값으로 덮어쓰기
  };

  const CustomCaption = () => (
    <div className="flex gap-sp80 items-center justify-between px-sp80 border-b py-sp80 border-semantic-secondary-border-normal-default">
      <Button
        variant={buttonStyles.navigation?.variant}
        state={buttonStyles.navigation?.state}
        onClick={handlePrevMonth}
      >
        <ChevronLeft size={20} />
      </Button>
      <Field>
        <FieldTrigger asChild>
          <SelectDropdownField
            mode="single"
            variant="transparent"
            value={currentDate.getFullYear().toString()}
            showPlaceholder={false}
          />
        </FieldTrigger>
        <FieldContent>
          <SelectDropdownList
            mode="single"
            options={years.map((year) => year.toString())}
            value={currentDate.getFullYear().toString()}
            onChange={(value) => handleYearChange(value as string)}
            checkStyle="check"
            hasOverflow={true}
          />
        </FieldContent>
      </Field>

      <Field>
        <FieldTrigger asChild>
          <SelectDropdownField
            mode="single"
            variant="transparent"
            value={months[currentDate.getMonth()]}
            showPlaceholder={false}
          />
        </FieldTrigger>
        <FieldContent>
          <SelectDropdownList
            mode="single"
            options={months}
            value={months[currentDate.getMonth()]}
            onChange={(value) => handleMonthChange(value as string, months)}
            checkStyle="check"
            hasOverflow={true}
          />
        </FieldContent>
      </Field>
      <Button
        variant={buttonStyles.navigation?.variant}
        state={buttonStyles.navigation?.state}
        onClick={handleNextMonth}
      >
        <ChevronRight size={20} />
      </Button>
    </div>
  );

  const handlePopoverClose = () => {
    handleCancel();
    setIsOpen(false);
  };

  const handlePopoverConfirm = () => {
    handleConfirm();
    setIsOpen(false);
  };

  const renderContent = () => {
    const textClassName =
      !selectedDate && !inputBuffer
        ? "text-semantic-secondary-foreground-inverse-default"
        : "";

    const dateContent = inputBuffer
      ? `${inputBuffer.padEnd(8, "X").slice(0, 4)}.${inputBuffer
          .padEnd(8, "X")
          .slice(4, 6)}.${inputBuffer.padEnd(8, "X").slice(6, 8)}`
      : selectedDate
        ? format(selectedDate, "yyyy.MM.dd", { locale: ko })
        : placeholder;

    const icon = (
      <Calendar
        size={defaultLayout.iconSize}
        className={cn(
          "text-semantic-secondary-foreground-strong-default",
          defaultLayout.iconClassName
        )}
      />
    );

    const contentClass = cn(
      "flex items-center gap-sp80 h-full",
      defaultLayout.containerClassName
    );

    return (
      <div className={contentClass}>
        {defaultLayout.iconPosition === "left" && icon}
        <div
          className={cn(
            "flex items-center",
            defaultLayout.textClassName,
            textClassName
          )}
        >
          {dateContent}
        </div>
        {defaultLayout.iconPosition === "right" && icon}
      </div>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={buttonStyles.trigger?.variant}
          state={buttonStyles.trigger?.state}
          size={size}
          className={cn("flex items-center", className)}
        >
          {renderContent()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="max-h-[80vh] overflow-auto">
          <CustomCaption />
          <div className="px-3">
            <DayPicker
              showOutsideDays={showOutsideDays}
              disabled={isDateDisabled}
              classNames={{
                months:
                  "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                month: "space-y-4",
                caption: "hidden",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center",
                nav_button: cn(
                  buttonVariants({ variant: "outline" }),
                  "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hidden"
                ),
                table: "w-full",
                head_row: "flex justify-between",
                head_cell:
                  "rounded-md w-10 h-10 typography-semantic-label-1-normal---regular text-semantic-secondary-foreground-normal-default flex items-center justify-center",
                row: "flex justify-between w-full mt-2",
                cell: cn(
                  "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
                  "[&:has([aria-selected])]:rounded-md"
                ),
                day: cn(
                  // buttonVariants({ variant: "transparent" }),
                  "size-9 p-0 font-normal aria-selected:opacity-100 rounded-full typography-semantic-label-1-normal---regular text-semantic-secondary-foreground-normal-default hover:bg-semantic-secondary-background-normal-hovered disabled:hover:cursor-not-allowed disabled:text-semantic-secondary-background-normal-hovered active:bg-semantic-secondary-background-normal-hovered"
                ),
                day_selected:
                  "bg-semantic-primary-background-normal-default text-semantic-static-global-global-white hover:bg-primary hover:text-semantic-static-global-global-white focus:bg-semantic-primary-background-normal-default focus:text-primary-foreground",
                day_today:
                  "border border-semantic-primary-background-normal-default",
                day_outside:
                  "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                day_disabled:
                  "text-semantic-disabled-foreground-global-default typography-semantic-label-1-normal---regular",
                day_hidden: "invisible",
                ...classNames,
              }}
              mode="single"
              selected={tempSelectedDate}
              // @ts-ignore
              onSelect={handleDaySelect}
              month={currentDate}
              locale={ko}
              {...props}
            />
          </div>
          <div className="flex justify-end p-sp160 border-t border-semantic-secondary-border-normal-default gap-sp80">
            <Button
              variant={buttonStyles.action.variant}
              state={buttonStyles.action.state}
              onClick={handlePopoverClose}
            >
              취소
            </Button>
            <Button
              variant={buttonStyles.action.variant}
              state={buttonStyles.action.state}
              onClick={handlePopoverConfirm}
            >
              확인
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PopoverCalendar;
